import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Loader2, Printer } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, apiError } from "../lib/api";
import { formatARS } from "../lib/site";
import { PAY } from "../components/traffic/CashPanel";

const Row = ({ k, v }) => <><dt className="text-black/60">{k}</dt><dd className="font-semibold">{v}</dd></>;
const when = (s) => (s ? new Date(s).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "America/Argentina/Buenos_Aires" }) : "");
const Sign = ({ label }) => <div className="pt-10 text-center text-xs"><div className="border-t border-black" /><p className="mt-1">{label}</p></div>;

const Copy = ({ b, who }) => {
  const rf = b.refund ?? {};
  return (
    <div className="break-inside-avoid rounded-3xl border-2 border-black/40 bg-white p-6 text-black print:rounded-none">
      <div className="flex items-start justify-between gap-3">
        <img src="/brand/logo-green.png" alt="Santiagueño" className="h-9 w-auto" />
        <div className="text-right text-xs"><p className="font-bold uppercase">Comprobante de devolución</p><p className="font-mono">Nº {b.code}-D</p><p>{who}</p></div>
      </div>
      <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <Row k="Fecha de devolución" v={when(rf.refunded_at)} />
        <Row k="Titular" v={`${b.name} · DNI ${b.dni || "—"}`} />
        <Row k="Viaje" v={`${b.origin} → ${b.destination} · ${b.date} ${b.departure} hs`} />
        <Row k="Butacas" v={(b.seats ?? []).map((s) => (b.pax?.[s] ? `${s} (${b.pax[s].name})` : s)).join(", ")} />
        <Row k="Pagado" v={formatARS(b.total)} />
        <Row k="Condición" v={rf.reason} />
        <Row k="Devuelto" v={<span data-testid="receipt-amount" className="text-lg">{formatARS(rf.amount)} ({rf.pct}%)</span>} />
        <Row k="Forma" v={`${rf.payment === "nave" ? "Reintegro a la tarjeta por NAVE" : PAY[rf.payment] ?? rf.payment}${rf.cbu_alias ? ` · CBU/alias ${rf.cbu_alias}` : ""}`} />
        <Row k="Boletería" v={`${rf.office ?? ""} · atendió ${rf.refunded_by ?? ""}`} />
      </dl>
      <p className="mt-4 text-xs">El pasaje {b.code} queda anulado y sin validez para viajar.</p>
      <div className="mt-2 grid grid-cols-3 gap-4"><Sign label="Firma del pasajero" /><Sign label="Aclaración y DNI" /><Sign label="Firma y sello boletería" /></div>
    </div>
  );
};

export default function AdminDevolucion() {
  const { id } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { if (user) api.get(`/admin/bookings/${id}`).then((r) => setB(r.data)).catch((e) => setErr(apiError(e))); }, [user, id]);

  if (user === false) return <Navigate to="/admin" replace />;
  if (err) return <p data-testid="receipt-error" className="p-10 text-red-700">{err}</p>;
  if (!b) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (b.refund?.status !== "devuelto") return <p data-testid="receipt-error" className="p-10">Este pasaje no tiene una devolución hecha.</p>;

  return (
    <section data-testid="refund-receipt-page" className="mx-auto max-w-2xl space-y-6 px-5 py-10 print:max-w-none print:space-y-4 print:p-0">
      <button onClick={() => window.print()} data-testid="receipt-print" className="inline-flex items-center gap-2 rounded-full bg-[var(--forest)] px-5 py-2.5 text-sm font-bold text-white print:hidden"><Printer className="h-4 w-4" /> Imprimir comprobante</button>
      <Copy b={b} who="Original para el pasajero" />
      <div className="border-t-2 border-dashed border-black/30 print:my-2" />
      <Copy b={b} who="Duplicado para la boletería" />
    </section>
  );
}
